// src/pages/WebinarFunnel/WebinarFunnel.jsx
import React, { useState, useEffect } from 'react';
import { useCampaignData } from '../../hooks/useCampaignData';

import { StandardHeader } from '../../components/common/StandardHeader';
import KeyInsights from '../../components/webinar_funnel/sections/KeyInsights';
import SourceSummary from '../../components/webinar_funnel/sections/SourceSummary';
import CampaignPerformance from '../../components/webinar_funnel/sections/CampaignPerformance';
import DetailedChartModal from '../../components/webinar_funnel/modals/DetailedChartModal';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import { LoadingSpinner, ErrorMessage } from '../../components/common';
import { DataExplorer } from '../../components/webinar_funnel/sections';
import { formatLastUpdated } from '../../utils/dataUtils';

const WebinarFunnel = () => {
  const {
    data, loading, refreshing, error,
    summaryStats, initialized, lastUpdated, refetch,
  } = useCampaignData();

  const [showChartModal,   setShowChartModal]   = useState(false);
  const [selectedMetric,   setSelectedMetric]   = useState(null);

  const [insightsCollapsed,     setInsightsCollapsed]     = useState(false);
  const [sourceSummaryCollapsed,setSourceSummaryCollapsed]= useState(false);
  const [campaignCollapsed,     setCampaignCollapsed]     = useState(false);
  const [explorerCollapsed,     setExplorerCollapsed]     = useState(true);

  // Page title + favicon
  useEffect(() => {
    document.title = 'Pulse: Webinar Funnel';
  }, []);

  const handleCardClick = (title, value, campaignData, metricKey, chartType, color) => {
    setSelectedMetric({ title, value, campaignData, metricKey, chartType, color });
    setShowChartModal(true);
  };

  // ── Initial load spinner ──────────────────────────────────────────────────
  if ((loading && !initialized) || (refreshing && (!data || data.length === 0))) {
    return <LoadingSpinner />;
  }

  // ── Error (no cached data) ────────────────────────────────────────────────
  if (error && (!data || data.length === 0)) {
    return <ErrorMessage message={error} onRetry={refetch} />;
  }

  // ── No data yet ───────────────────────────────────────────────────────────
  if (initialized && !loading && !refreshing && (!data || !Array.isArray(data) || data.length === 0)) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-grey-50">
      <div className="max-w-full mx-auto space-y-3">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <StandardHeader
          title="Webinar Funnel"
          subtitle="Comprehensive analytics for webinar funnel"
          isRefreshing={refreshing}
          onRefresh={refetch}
          refreshButtonText="Refresh"
          lastUpdated={lastUpdated}
        />

        {/* ── Refresh banner ───────────────────────────────────────────────── */}
        {refreshing && (
          <div className="bg-brightblue-50 border border-brightblue-200 rounded-lg px-4 py-2.5 flex items-center justify-center gap-2">
            <div className="w-3.5 h-3.5 border-2 border-brightblue-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-brightblue-700">Refreshing data…</span>
          </div>
        )}

        {/* ── Error banner (with cached data) ─────────────────────────────── */}
        {error && data && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-yellow-700">⚠️ Error refreshing data: {error}</span>
            <button onClick={refetch} className="text-xs text-yellow-700 hover:text-yellow-900 underline font-medium">
              Retry
            </button>
          </div>
        )}

        {/* ── Key Insights ────────────────────────────────────────────────── */}
        <CollapsibleSection
          title="Key Insights"
          subtitle="Performance highlights and top metrics"
          collapsed={insightsCollapsed}
          setCollapsed={setInsightsCollapsed}
        >
          <KeyInsights data={data} />
        </CollapsibleSection>

        {/* ── Source Summary ───────────────────────────────────────────────── */}
        <CollapsibleSection
          title="Webinar Funnel"
          subtitle="Conversion flow across all campaigns"
          collapsed={sourceSummaryCollapsed}
          setCollapsed={setSourceSummaryCollapsed}
        >
          <SourceSummary data={data} onCardClick={handleCardClick} />
        </CollapsibleSection>

        {/* ── Campaign Performance ─────────────────────────────────────────── */}
        <CollapsibleSection
          title="Campaign Performance"
          subtitle="Showing data by campaigns"
          collapsed={campaignCollapsed}
          setCollapsed={setCampaignCollapsed}
        >
          <CampaignPerformance data={data} />
        </CollapsibleSection>

        {/* ── Campaign Explorer ────────────────────────────────────────────── */}
        <CollapsibleSection
          title="Campaign Explorer"
          subtitle="Interactive pivot analysis with custom field selection"
          collapsed={explorerCollapsed}
          setCollapsed={setExplorerCollapsed}
        >
          <DataExplorer data={data} />
        </CollapsibleSection>

        {/* ── Chart modal ──────────────────────────────────────────────────── */}
        {showChartModal && selectedMetric && (
          <DetailedChartModal
            data={selectedMetric}
            onClose={() => setShowChartModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default WebinarFunnel;